import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';

export type TransactionType = 'INCOME' | 'EXPENSE';

export type TransactionVault =
  | 'CASH_BRANCH'
  | 'INSTAPAY'
  | 'VODAFONE_CASH'
  | 'ORANGE_CASH'
  | 'ETISALAT_CASH'
  | 'WE_PAY'
  | 'BANK_ACCOUNT';

export interface TreasuryTransaction {
  id: string;
  type: TransactionType;
  category: string;
  categoryLabelAr: string;
  amount: number;
  vault: TransactionVault;
  description: string;
  referenceNumber?: string;
  recordedBy: string;
  date: string;
  createdAt: string;
  status: 'CONFIRMED' | 'PENDING';
}

export interface TreasuryStats {
  netBalance: number;
  totalIncome: number;
  totalExpense: number;
  vaultBalances: {
    cashBranch: number;
    instapay: number;
    vodafoneCash: number;
    otherVaults: number;
  };
  transactionsCount: number;
}

export const VAULT_LABELS: Record<TransactionVault, string> = {
  CASH_BRANCH: 'خزينة الفرع (كاش)',
  INSTAPAY: 'إنستاباي (حساب بنكي)',
  VODAFONE_CASH: 'فودافون كاش',
  ORANGE_CASH: 'أورنج كاش',
  ETISALAT_CASH: 'اتصالات كاش',
  WE_PAY: 'وي باي (WE Pay)',
  BANK_ACCOUNT: 'تحويل بنكي مباشر',
};

export const INCOME_CATEGORIES = [
  { id: 'BOOKING_PAYMENT', label: 'تحصيل حجز رحلة طالب' },
  { id: 'MONTHLY_PASS', label: 'اشتراك شهري مجمع' },
  { id: 'OPENING_BALANCE', label: 'رصيد افتتاحي للخزينة' },
  { id: 'CAPITAL_INJECTION', label: 'إيداع رأس مال / تمويل' },
  { id: 'OTHER_INCOME', label: 'إيرادات وتحصيلات أخرى' },
];

export const EXPENSE_CATEGORIES = [
  { id: 'FUEL', label: 'وقود وسولار الأتوبيسات' },
  { id: 'DRIVER_PAY', label: 'مستحقات ويوميات السائقين' },
  { id: 'FLEET_MAINTENANCE', label: 'صيانة وإصلاحات الأسطول' },
  { id: 'TOLL_ROADS', label: 'كارتات وبوابات الطرق' },
  { id: 'OFFICE_EXPENSE', label: 'مصاريف ومستلزمات المقر' },
  { id: 'SALARIES', label: 'مرتبات الإدارة والمشرفين' },
  { id: 'REFUND', label: 'استرداد مبالغ لطلاب (Refund)' },
  { id: 'OTHER_EXPENSE', label: 'مصروفات تشغيلية أخرى' },
];

const LOCAL_STORAGE_KEY = 'oto_treasury_transactions_v1';

export const treasuryService = {
  /**
   * Fetch all treasury transactions from Firestore (with local cache fallback)
   */
  async getAllTransactions(): Promise<TreasuryTransaction[]> {
    try {
      const colRef = collection(db, 'treasuryTransactions');
      const q = query(colRef, orderBy('date', 'desc'), limit(200));
      const snap = await getDocs(q);

      if (!snap.empty) {
        const list: TreasuryTransaction[] = [];
        snap.forEach((d) => {
          list.push({ ...(d.data() as TreasuryTransaction), id: d.id });
        });
        if (typeof window !== 'undefined') {
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(list));
        }
        return list;
      }
    } catch (err) {
      console.warn('Firestore treasury fetch failed, using local backup:', err);
    }

    // Local fallback - filter out any legacy fake seed data
    if (typeof window !== 'undefined') {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        try {
          const parsed: TreasuryTransaction[] = JSON.parse(raw);
          const clean = Array.isArray(parsed)
            ? parsed.filter((t) => t && !t.id?.startsWith('tx_seed_'))
            : [];
          if (clean.length !== (Array.isArray(parsed) ? parsed.length : 0)) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(clean));
          }
          return clean;
        } catch (_) {}
      }
    }

    return [];
  },

  /**
   * Add a new transaction (Income or Expense)
   */
  async addTransaction(
    input: Omit<TreasuryTransaction, 'id' | 'createdAt'>
  ): Promise<TreasuryTransaction> {
    const id = `tx_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const newTx: TreasuryTransaction = {
      ...input,
      id,
      createdAt: new Date().toISOString(),
    };

    // 1. Try Firestore
    try {
      const docRef = doc(db, 'treasuryTransactions', id);
      await setDoc(docRef, newTx);
    } catch (err) {
      console.warn('Firestore write failed, saving to local state:', err);
    }

    // 2. Save locally
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        const existing: TreasuryTransaction[] = raw ? JSON.parse(raw) : [];
        const updated = [newTx, ...existing];
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
      } catch (_) {}
    }

    return newTx;
  },

  /**
   * Delete a transaction
   */
  async deleteTransaction(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'treasuryTransactions', id));
    } catch (err) {
      console.warn('Firestore delete failed:', err);
    }

    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (raw) {
          const existing: TreasuryTransaction[] = JSON.parse(raw);
          const filtered = existing.filter((t) => t.id !== id);
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtered));
        }
      } catch (_) {}
    }
  },

  /**
   * Compute comprehensive stats
   */
  calculateStats(transactions: TreasuryTransaction[]): TreasuryStats {
    let totalIncome = 0;
    let totalExpense = 0;
    let cashBranch = 0;
    let instapay = 0;
    let vodafoneCash = 0;
    let otherVaults = 0;

    for (const tx of transactions) {
      const amt = Number(tx.amount) || 0;
      if (tx.type === 'INCOME') {
        totalIncome += amt;
        if (tx.vault === 'CASH_BRANCH') cashBranch += amt;
        else if (tx.vault === 'INSTAPAY') instapay += amt;
        else if (tx.vault === 'VODAFONE_CASH') vodafoneCash += amt;
        else otherVaults += amt;
      } else {
        totalExpense += amt;
        if (tx.vault === 'CASH_BRANCH') cashBranch -= amt;
        else if (tx.vault === 'INSTAPAY') instapay -= amt;
        else if (tx.vault === 'VODAFONE_CASH') vodafoneCash -= amt;
        else otherVaults -= amt;
      }
    }

    return {
      netBalance: totalIncome - totalExpense,
      totalIncome,
      totalExpense,
      vaultBalances: {
        cashBranch,
        instapay,
        vodafoneCash,
        otherVaults,
      },
      transactionsCount: transactions.length,
    };
  },
};
